const URL_ = "/poetry/words-v1.txt.gz";
const CACHE = "lf-poetry-v1";
const words = new Map();
const byTail = new Map();
const byVowel = new Map();
let custom = new Map();
let loading = null;
let ready = false;
const listeners = new Set();

export const onDictChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((f) => f());
export const dictReady = () => ready;

const fetchText = async () => {
  let res = null;
  try { const c = await caches.open(CACHE); res = await c.match(URL_); if (!res) { res = await fetch(URL_); if (res.ok) await c.put(URL_, res.clone()); } } catch { res = await fetch(URL_); }
  const buf = new Uint8Array(await res.arrayBuffer());
  if (buf[0] !== 0x1f || buf[1] !== 0x8b) return new TextDecoder().decode(buf);
  return new Response(new Blob([buf]).stream().pipeThrough(new DecompressionStream("gzip"))).text();
};

const push = (map, k, w) => { const l = map.get(k); if (l) l.push(w); else map.set(k, [w]); };

export const loadDict = () => {
  if (!loading) {
    loading = fetchText().then((text) => {
      text.split("\n").forEach((line, rank) => {
        const [w, syl, tail] = line.split("\t");
        if (!w) return;
        words.set(w, { syl: Number(syl.split("/")[0]), tail, rank });
        push(byTail, tail, w);
        push(byVowel, tail.split(" ")[0], w);
      });
      ready = true;
      emit();
    }).catch((e) => { loading = null; throw e; });
  }
  return loading;
};

export const getCustom = () => [...custom.values()];
export const setCustom = (list) => { custom = new Map((list || []).map((p) => [p.word, p])); emit(); };

const clean = (w) => String(w || "").toLowerCase().replace(/[’‘]/g, "'").replace(/^'+|'+$/g, "").replace(/[^a-z']/g, "");

export const guessSyllables = (w) => {
  w = clean(w).replace(/'/g, "");
  if (!w) return 0;
  if (w.length <= 3) return 1;
  let s = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  const n = (s.match(/[aeiouy]{1,2}/g) || []).length;
  return Math.max(1, n + (/[^aeiouy]le$/.test(w) ? 1 : 0));
};

export const syllables = (w) => {
  const k = clean(w);
  if (!k) return 0;
  return custom.get(k)?.syllables || words.get(k)?.syl || words.get(k.replace(/'s$/, ""))?.syl || guessSyllables(k);
};

export const known = (w) => { const k = clean(w); return custom.has(k) || words.has(k); };
export const wordRank = (w) => words.get(w)?.rank;

const guessTail = (w) => { const m = w.match(/[aeiouy]+[^aeiouy]*e?$/); return `~${m ? m[0] : w.slice(-3)}`; };

export const rhymeKey = (w) => {
  const k = clean(w);
  if (!k) return "";
  const c = custom.get(k);
  if (c?.rhymes_like) { const like = clean(c.rhymes_like.split(" ").pop()); if (like !== k) return rhymeKey(like); }
  return words.get(k)?.tail || words.get(k.replace(/'s$/, ""))?.tail || guessTail(k);
};

const sameEnd = (a, b) => a.split(" ").pop() === b.split(" ").pop();
const COMMON = 40000;
const CLASSES = { M: "n", N: "n", NG: "n", P: "p", B: "p", T: "p", D: "p", K: "p", G: "p", F: "f", V: "f", TH: "f", DH: "f", S: "f", Z: "f", SH: "f", ZH: "f", CH: "f", JH: "f", L: "l", R: "l" };
const classOf = (t) => CLASSES[t.split(" ").pop()] || "v";

export const findRhymes = (w, limit = 60) => {
  const k = clean(w);
  const key = rhymeKey(k);
  if (!key || key.startsWith("~")) return { key, perfect: [], near: [] };
  const skip = (x) => x === k || x.endsWith(k) || k.endsWith(x) || x.includes("'");
  const common = (list) => { const c = list.filter((x) => words.get(x).rank < COMMON); return c.length >= 8 ? c : list; };
  const perfect = common((byTail.get(key) || []).filter((x) => !skip(x))).slice(0, limit);
  const vowel = key.split(" ")[0];
  const len = key.split(" ").length;
  const near = common((byVowel.get(vowel) || []).filter((x) => !skip(x) && words.get(x).tail !== key && Math.abs(words.get(x).tail.split(" ").length - len) <= 1))
    .map((x) => { const t = words.get(x).tail; return { x, s: (sameEnd(t, key) ? 2 : 0) + (t.split(" ").length === len ? 2 : 0) + (classOf(t) === classOf(key) ? 1.5 : 0) - words.get(x).rank / 15000 }; })
    .sort((a, b) => b.s - a.s).slice(0, limit).map((o) => o.x);
  custom.forEach((p) => { if (p.word !== k && rhymeKey(p.word) === key && !perfect.includes(p.word)) perfect.unshift(p.word); });
  return { key, perfect, near };
};
